import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { apiJson } from "@/lib/api";

export interface SocleItem { key: string; titre: string; type: string; page: string; aide: string; etat: string; detail: string; apercu?: string; contenu_present?: boolean; corrections?: { at: string; texte: string }[] }
export interface Socle { items: SocleItem[]; pret: number; total: number; pct: number; peut_deployer: boolean; manquants: string[]; instructeur?: boolean }

const ETAT: Record<string, { l: string; c: string }> = {
  pret: { l: "Prêt", c: "border-emerald-500/40 text-emerald-500" },
  a_valider: { l: "À valider", c: "border-amber-500/40 text-amber-500" },
  a_corriger: { l: "À corriger", c: "border-destructive/40 text-destructive" },
  partiel: { l: "Partiel", c: "border-sky-500/40 text-sky-500" },
  absent: { l: "Manquant", c: "border-border text-muted-foreground" },
};

export default function SocleSection({ socle, onChange }: { socle: Socle; onChange: (s: Socle) => void }) {
  const navigate = useNavigate();
  const [note, setNote] = useState<Record<string, string>>({});
  const [open, setOpen] = useState<string>("");
  const [busy, setBusy] = useState("");

  const act = async (key: string, action: "valider" | "corriger") => {
    setBusy(key);
    try {
      onChange(await apiJson<Socle>(`/api/value/socle/${key}`, { method: "POST", body: JSON.stringify({ action, note: note[key] }) }));
      toast.success(action === "valider" ? "Validé : Grafton peut s'appuyer dessus" : "Correction enregistrée");
      setNote({ ...note, [key]: "" });
    } catch (e) { toast.error((e as Error)?.message?.includes("Rien à valider") ? "Renseignez d'abord ce contenu." : action === "corriger" ? "Décrivez la correction à apporter." : "Action impossible"); }
    finally { setBusy(""); }
  };

  return (
    <Card data-testid="socle">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-primary" />Socle de données fiables</CardTitle>
        <CardDescription>Grafton ne déploie rien sur des données qu'il ne peut pas vérifier. Les trois piliers doivent être validés par vous ; les accès permettent de mesurer le réel.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1">
          <div className="flex items-center justify-between text-sm"><span>Fiabilité du socle : <b>{socle.pret} sur {socle.total}</b> éléments prêts</span><span className={socle.peut_deployer ? "text-emerald-500" : socle.instructeur ? "text-sky-500" : "text-amber-500"}>{socle.peut_deployer ? "Déploiement possible" : socle.instructeur ? "Mode instructeur" : "Déploiement bloqué"}</span></div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-primary" style={{ width: `${socle.pct}%` }} /></div>
          {!socle.peut_deployer && !socle.instructeur && <p className="text-xs text-muted-foreground">À valider d'abord : {socle.manquants.join(" ; ")}.</p>}
          {!socle.peut_deployer && socle.instructeur && <p className="text-xs text-sky-500" data-testid="mode-instructeur">Mode instructeur : le verrou ne vous bloque pas. Chaque autorisation ou déploiement sans socle validé est journalisé (piliers non validés : {socle.manquants.join(" ; ")}).</p>}
        </div>
        {socle.items.map((it) => (
          <div key={it.key} className="rounded-lg border border-border p-3 space-y-2" data-testid={`socle-${it.key}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0"><p className="text-sm font-medium">{it.titre}</p><p className="text-[11px] text-muted-foreground">{it.detail}</p></div>
              <div className="flex items-center gap-2"><Badge variant="outline" className={`text-[10px] ${ETAT[it.etat]?.c || ""}`}>{ETAT[it.etat]?.l || it.etat}</Badge>
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => navigate(it.page)}><ExternalLink className="h-3 w-3 mr-1" />{it.type === "valide" ? "Compléter" : "Connecter"}</Button>
                {it.type === "valide" && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen(open === it.key ? "" : it.key)}>{open === it.key ? "Fermer" : "Relire"}</Button>}</div>
            </div>
            {open === it.key && it.type === "valide" && (
              <div className="space-y-2">
                <p className="text-[11px] text-muted-foreground">{it.aide}</p>
                <div className="text-sm rounded-md bg-secondary/40 p-3 whitespace-pre-wrap">{it.apercu || "Rien de renseigné pour le moment."}</div>
                {(it.corrections?.length || 0) > 0 && <ul className="text-[11px] text-muted-foreground list-disc pl-4">{it.corrections!.slice(-3).map((c, i) => <li key={i}>{new Date(c.at).toLocaleDateString("fr-FR")} : {c.texte}</li>)}</ul>}
                <Textarea rows={2} className="text-sm" placeholder="Une erreur à corriger ? Décrivez-la ici." value={note[it.key] || ""} onChange={(e) => setNote({ ...note, [it.key]: e.target.value })} />
                <div className="flex gap-2"><Button size="sm" disabled={busy === it.key || !it.contenu_present} onClick={() => act(it.key, "valider")}>Je valide</Button><Button size="sm" variant="outline" disabled={busy === it.key} onClick={() => act(it.key, "corriger")}>Signaler une correction</Button></div>
              </div>
            )}
            {open !== it.key && it.type !== "valide" && <p className="text-[11px] text-muted-foreground">{it.aide}</p>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
